import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CircleAlert,
  CircleCheck,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/lib/hooks/use-toast';
import { MIN_PASSWORD_LENGTH } from '@/lib/utils/constants';
import { authService } from '../services/auth-service';
import { resetPasswordSchema, ResetPasswordFormData } from '../schemas/auth.schemas';
import { CAMPO_DE_INGRESO, ICONO_DE_CAMPO, ROTULO_DE_CAMPO } from './marco-de-ingreso';

/** Crear la contraseña nueva con el enlace del correo (`/reset-password?token=…`). */
export function ResetPasswordForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const invitacion = searchParams.get('invitacion') === '1';

  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setIsLoading(true);
      await authService.resetPassword({
        token,
        password: data.password,
        confirmPassword: data.confirmPassword,
      });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (error: any) {
      toast({
        title: 'No se pudo cambiar la contraseña',
        description:
          error.response?.data?.message || 'El enlace no es válido o ya venció. Pide uno nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const volver = (
    <Link
      to="/login"
      className="inline-flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      Volver a ingresar
    </Link>
  );

  if (!token) {
    return (
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex size-16 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
          <CircleAlert className="size-8" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="font-titulo text-2xl font-semibold tracking-tight">Enlace incompleto</h2>
          <p className="text-[15px] text-muted-foreground">
            Este enlace no trae el código para cambiar la contraseña. Pide uno nuevo.
          </p>
        </div>
        <Link
          to="/forgot-password"
          className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-degradado-prixia text-base font-semibold text-primary-foreground shadow-md transition hover:brightness-110"
        >
          Pedir un enlace nuevo
        </Link>
        {volver}
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex size-16 items-center justify-center rounded-2xl bg-ok-suave text-ok">
          <CircleCheck className="size-8" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="font-titulo text-2xl font-semibold tracking-tight">
            {invitacion ? '¡Listo, ya tienes tu contraseña!' : '¡Listo, contraseña cambiada!'}
          </h2>
          <p className="text-[15px] text-muted-foreground">Te llevamos a ingresar…</p>
        </div>
        <Link
          to="/login"
          className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-degradado-prixia text-base font-semibold text-primary-foreground shadow-md transition hover:brightness-110"
        >
          Ingresar ahora
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
      <div className="flex flex-col gap-2.5">
        <Label htmlFor="password" className={ROTULO_DE_CAMPO}>
          Contraseña nueva
        </Label>
        <div className="relative">
          <LockKeyhole className={ICONO_DE_CAMPO} aria-hidden="true" />
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder={`Al menos ${MIN_PASSWORD_LENGTH} caracteres`}
            {...register('password')}
            error={errors.password?.message}
            disabled={isLoading}
            className={`${CAMPO_DE_INGRESO} pr-12`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-6 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            {showPassword ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        <Label htmlFor="confirmPassword" className={ROTULO_DE_CAMPO}>
          Repite la contraseña
        </Label>
        <div className="relative">
          <LockKeyhole className={ICONO_DE_CAMPO} aria-hidden="true" />
          <Input
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="La misma de arriba"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
            disabled={isLoading}
            className={CAMPO_DE_INGRESO}
          />
        </div>
      </div>
      <Button
        type="submit"
        variant="degradado"
        className="mt-1 h-[52px] w-full rounded-xl text-base"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="size-[18px] animate-spin" aria-hidden="true" />
            Guardando…
          </>
        ) : invitacion ? (
          'Crear mi contraseña'
        ) : (
          'Guardar contraseña nueva'
        )}
      </Button>
      {volver}
    </form>
  );
}

export default ResetPasswordForm;
